import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList, ReferenceLine } from 'recharts';

const PdfVerticalStackedBarChart = ({ 
  data = [], 
  xAxisLabel = 'Time', 
  yAxisLabel = 'No. of submissions', 
  colors = ['#003C73', '#76C8E8', '#B0EAC0','#FBCF4E','#F6A4A4','#C9B0D5'],
  height = 400,
  activityTypes = ['Coding', 'Data science', 'Sampling'],
  showLegend = true
}) => {
  // Transform data for stacked bars (raw and chart versions)
  const transformedDataRaw = data.map((item) => {
    const result = { name: item.name };
    activityTypes.forEach((type) => {
      result[type] = item[type] || 0;
    });
    return result;
  });

  // Replace zeros with null for rendering so segments with 0 are not drawn
  const transformedData = transformedDataRaw.map(row => {
    const out = { name: row.name };
    activityTypes.forEach((type) => {
      const v = row[type] || 0;
      out[type] = v > 0 ? v : null;
    })
    return out;
  })

  // Compute dynamic domain and ticks for Y scale from TOTALS
  const totals = Array.isArray(data)
    ? data.map(d => activityTypes.reduce((sum, type) => sum + Number(d?.[type] ?? 0), 0))
    : [];
  const maxTotal = totals.length ? Math.max(...totals, 0) : 0;
  // Round up: <=5 -> 5; otherwise to the next multiple of 5 (e.g., 24->25, 37->40)
  const roundedMax = maxTotal <= 5
    ? 5
    : Math.ceil(maxTotal / 5) * 5;
  const step = Math.max(1, Math.round(roundedMax / 5)); // 5 divisions
  const computedTicks = [];
  for (let t = 0; t <= roundedMax; t += step) {
    computedTicks.push(t);
  }
  const computedDomain = [0, roundedMax];

  // Custom label for segment values (only when > 0), for stacked bars
  const renderSegmentLabel = (dataKey) => (props) => {
    const { x, y, width, height, index } = props || {};
    if (index == null) return null;
    const dataPoint = transformedDataRaw[index] || {};
    const individualValue = dataPoint[dataKey] || 0;
    if (!individualValue) return null;
    return (
      <text
        x={(x ?? 0) + (width ?? 0) / 2}
        y={(y ?? 0) + (height ?? 0) / 2}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="12"
        fontWeight="500"
      >
        {individualValue}
      </text>
    );
  };

  // Format X-axis month values like June,2024 from YYYY-MM or YYYY-MM-DD
  const formatMonthYearFull = (value) => {
    if (!value || typeof value !== 'string') return value;
    const match = value.match(/^(\d{4})-(\d{2})/);
    if (!match) return value;
    const year = match[1];
    const monthIdx = Number(match[2]) - 1;
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    if (monthIdx < 0 || monthIdx > 11) return value;
    return `${months[monthIdx]},${year}`;
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full flex items-center justify-center text-gray-500" style={{ height: `${height}px` }}>
        <div className="text-sm">No data available</div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col h-full">
      <div style={{ flex: 1, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{
              top: 20,
              right: 20,
              left: 20,
              bottom: 40,
            }}
          >
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: '#666', strokeWidth: 1 }}
              tickLine={{ stroke: '#666', strokeWidth: 1 }}
              tickFormatter={formatMonthYearFull}
              label={{ value: xAxisLabel, position: 'bottom', offset: 10 }}
            />
            <YAxis
              type="number"
              domain={computedDomain}
              ticks={computedTicks}
              width={70}
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={{ stroke: '#666', strokeWidth: 1 }}
              tickLine={{ stroke: '#666', strokeWidth: 1 }}
              label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', offset: 0 }}
            />
            {/* Dashed grid lines for non-zero ticks only across the bars area */}
            {computedTicks.filter(tick => tick !== 0).map((tick) => (
              <ReferenceLine 
                key={`main-${tick}`}
                y={tick} 
                stroke="#e5e7eb" 
                strokeDasharray="3 3" 
                strokeWidth={1}
              />
            ))}
            {activityTypes.map((type, index) => (
              <Bar 
                key={type}
                dataKey={type} 
                stackId="a"
                fill={colors[index % colors.length]}
                maxBarSize={81}
              >
                <LabelList content={renderSegmentLabel(type)} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Compact Legend */}
      {/* {showLegend && (
        <div className="mt-2 px-2" style={{ minHeight: '25px' }}>
          <div className="flex justify-center gap-3 flex-wrap">
            {activityTypes.map((type, index) => (
              <div key={type} className="flex items-center gap-1">
                <div 
                  className="w-2 h-2 rounded"
                  style={{ backgroundColor: colors[index % colors.length] }}
                ></div>
                <span className="text-xs">{type}</span>
              </div>
            ))}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default PdfVerticalStackedBarChart;
